/**
 * Google OAuth authentication utilities for Cognito integration
 */

import { Auth } from 'aws-amplify';

export interface GoogleAuthResult {
  user: any;
  isNewUser: boolean;
  needsAccountLinking?: boolean;
}

export interface GoogleAuthError {
  code: string;
  message: string;
  details?: any;
}

/**
 * Initiates Google OAuth sign-in flow through Cognito
 * @returns Promise that resolves when OAuth flow is initiated
 */
export async function signInWithGoogle(): Promise<void> {
  try {
    // Use Cognito's federated sign-in with Google
    await Auth.federatedSignIn({ provider: 'Google' as any });
  } catch (error: any) {
    console.error('Google sign-in error:', error);
    throw {
      code: error.code || 'GoogleSignInError',
      message: error.message || 'Failed to initiate Google sign-in',
      details: error
    } as GoogleAuthError;
  }
}

/**
 * Handles Google OAuth callback and processes the result
 * @returns GoogleAuthResult with user information and status
 */
export async function handleGoogleCallback(): Promise<GoogleAuthResult> {
  try {
    // Get the current authenticated user after OAuth callback
    const user = await Auth.currentAuthenticatedUser();
    
    // Check if this is a new user by looking at user attributes
    const attributes = await Auth.userAttributes(user);
    const attributeMap = attributes.reduce((acc, attr) => {
      acc[attr.Name] = attr.Value;
      return acc;
    }, {} as Record<string, string>);

    // Determine if this is a new user (no custom attributes set)
    const isNewUser = !attributeMap['custom:user_group'] || !attributeMap['custom:profile_complete'];

    return {
      user,
      isNewUser,
      needsAccountLinking: false // Will be implemented in account linking logic
    };
  } catch (error: any) {
    console.error('Google callback handling error:', error);
    throw {
      code: error.code || 'GoogleCallbackError',
      message: error.message || 'Failed to process Google authentication',
      details: error
    } as GoogleAuthError;
  }
}

/**
 * Links Google account to existing user account
 * @param existingEmail - Email of existing account to link
 * @returns Promise that resolves when accounts are linked
 */
export async function linkGoogleAccount(existingEmail: string): Promise<void> {
  try {
    // This would typically involve backend logic to merge accounts
    // For now, we'll implement basic linking through user attributes
    const user = await Auth.currentAuthenticatedUser();
    
    await Auth.updateUserAttributes(user, {
      'custom:linked_accounts': JSON.stringify(['google']),
      'custom:primary_email': existingEmail
    });
  } catch (error: any) {
    console.error('Google account linking error:', error);
    throw {
      code: error.code || 'GoogleLinkingError',
      message: error.message || 'Failed to link Google account',
      details: error
    } as GoogleAuthError;
  }
}

/**
 * Checks if current user has Google authentication linked
 * @returns boolean indicating if Google is linked
 */
export async function isGoogleLinked(): Promise<boolean> {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const attributes = await Auth.userAttributes(user);
    
    const linkedAccounts = attributes.find(attr => attr.Name === 'custom:linked_accounts');
    if (linkedAccounts?.Value) {
      const accounts = JSON.parse(linkedAccounts.Value);
      return accounts.includes('google');
    }
    
    return false;
  } catch (error) {
    console.error('Error checking Google link status:', error);
    return false;
  }
}

/**
 * Unlocks Google account from current user
 * @returns Promise that resolves when account is unlinked
 */
export async function unlinkGoogleAccount(): Promise<void> {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const attributes = await Auth.userAttributes(user);
    
    const linkedAccounts = attributes.find(attr => attr.Name === 'custom:linked_accounts');
    if (linkedAccounts?.Value) {
      const accounts = JSON.parse(linkedAccounts.Value);
      const updatedAccounts = accounts.filter((account: string) => account !== 'google');
      
      await Auth.updateUserAttributes(user, {
        'custom:linked_accounts': JSON.stringify(updatedAccounts)
      });
    }
  } catch (error: any) {
    console.error('Google account unlinking error:', error);
    throw {
      code: error.code || 'GoogleUnlinkingError',
      message: error.message || 'Failed to unlink Google account',
      details: error
    } as GoogleAuthError;
  }
}

/**
 * Gets user information from Google OAuth token
 * @param accessToken - Google access token
 * @returns User information from Google
 */
export async function getGoogleUserInfo(accessToken: string): Promise<any> {
  try {
    const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch Google user info');
    }
    
    return await response.json();
  } catch (error: any) {
    console.error('Error fetching Google user info:', error);
    throw {
      code: 'GoogleUserInfoError',
      message: 'Failed to fetch user information from Google',
      details: error
    } as GoogleAuthError;
  }
}

/**
 * Handles Google OAuth errors and provides user-friendly messages
 * @param error - Google OAuth error
 * @returns User-friendly error message
 */
export function getGoogleErrorMessage(error: GoogleAuthError): string {
  switch (error.code) {
    case 'popup_closed_by_user':
      return 'Google sign-in was cancelled. Please try again.';
    case 'access_denied':
      return 'Google sign-in was denied. Please grant permission to continue.';
    case 'network_error':
      return 'Network error occurred. Please check your connection and try again.';
    case 'GoogleSignInError':
      return 'Failed to sign in with Google. Please try again.';
    case 'GoogleCallbackError':
      return 'Failed to complete Google sign-in. Please try again.';
    case 'GoogleLinkingError':
      return 'Failed to link Google account. Please try again.';
    case 'GoogleUnlinkingError':
      return 'Failed to unlink Google account. Please try again.';
    default:
      return error.message || 'An error occurred with Google authentication. Please try again.';
  }
}